import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { SafeHtml } from "../components/SafeHtml";
import { ChevronDown, Shield, Mail, MessageCircle, Star, Gift, Zap, ChevronUp, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [brands, setBrands] = useState([]);
  const [banners, setBanners] = useState([]);
  const [modalAds, setModalAds] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeModals, setActiveModals] = useState([]);
  const [showModals, setShowModals] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [sequentialGroups, setSequentialGroups] = useState([]);
  const modalShownRef = useRef(false);
  const timedModalTimeouts = useRef([]);

  // Add AMP link to head when AMP is enabled
  useEffect(() => {
    if (settings.amp_enabled) {
      // Remove existing amphtml link if any
      const existingLink = document.querySelector('link[rel="amphtml"]');
      if (existingLink) existingLink.remove();
      
      // Add new amphtml link
      const ampLink = document.createElement('link');
      ampLink.rel = 'amphtml';
      ampLink.href = settings.amp_domain || `${window.location.origin}/api/amp`;
      document.head.appendChild(ampLink);
      
      return () => {
        const link = document.querySelector('link[rel="amphtml"]');
        if (link) link.remove();
      };
    }
  }, [settings.amp_enabled, settings.amp_domain]);

  useEffect(() => {
    fetchData();
    return () => {
      timedModalTimeouts.current.forEach(t => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    // Modal ads - show grouped modals sequentially
    if (modalAds.length > 0 && !modalShownRef.current) {
      // Group modals by group number
      const groupedModals = {};
      modalAds.forEach(modal => {
        const group = modal.group || 1;
        if (!groupedModals[group]) {
          groupedModals[group] = [];
        }
        groupedModals[group].push(modal);
      });

      // Sort each group by order
      Object.keys(groupedModals).forEach(group => {
        groupedModals[group].sort((a, b) => (a.order || 0) - (b.order || 0));
      });

      // Get sorted group keys (1, 2, 3, ...)
      const sortedGroupKeys = Object.keys(groupedModals).sort((a, b) => parseInt(a) - parseInt(b));
      
      // Build sequential groups array - each group contains modals to show together
      const groups = [];
      sortedGroupKeys.forEach(groupKey => {
        const groupModals = groupedModals[groupKey].filter(
          m => m.trigger_type === "onload" || m.trigger_type === "sequential"
        );
        if (groupModals.length > 0) {
          groups.push(groupModals);
        }
      });

      // Store all sequential groups for navigation
      setSequentialGroups(groups);

      // Show first group if exists
      if (groups.length > 0) {
        setTimeout(() => {
          setActiveModals(groups[0]);
          setCurrentGroupIndex(0);
          setShowModals(true);
          modalShownRef.current = true;
        }, 500);
      }

      // Setup timed modals (these work independently)
      const timedModals = modalAds.filter(m => m.trigger_type === "timed");
      timedModals.forEach((modal) => {
        const timeout = setTimeout(() => {
          setActiveModals([modal]);
          setShowModals(true);
        }, (modal.delay_seconds || 15) * 1000);
        timedModalTimeouts.current.push(timeout);
      });
    }
  }, [modalAds]);

  const fetchData = async () => {
    try {
      const [bannersRes, modalRes, settingsRes, brandsRes] = await Promise.all([
        axios.get(`${API}/banners?position=header`),
        axios.get(`${API}/modal-ads`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/brands`)
      ]);
      setBanners(bannersRes.data);
      setModalAds(modalRes.data);
      setSettings(settingsRes.data);
      setBrands(brandsRes.data);
      setLoading(false);
      
      // Update SEO meta tags
      const seoData = settingsRes.data;
      if (seoData.seo_title) {
        document.title = seoData.seo_title;
      } else if (seoData.site_name) {
        document.title = `${seoData.site_name} - En İyi Online Casino Siteleri`;
      }
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      if (seoData.seo_description) {
        metaDesc.content = seoData.seo_description;
      }
      
      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      if (seoData.seo_keywords) {
        metaKeywords.content = seoData.seo_keywords;
      }

      // Update canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (seoData.canonical_url) {
        if (!canonicalLink) {
          canonicalLink = document.createElement('link');
          canonicalLink.rel = 'canonical';
          document.head.appendChild(canonicalLink);
        }
        canonicalLink.href = seoData.canonical_url;
      } else if (canonicalLink) {
        canonicalLink.remove();
      }

      // Update hreflang tags
      // First remove existing hreflang links
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
      
      // Add new hreflang links
      if (seoData.hreflang_tags && seoData.hreflang_tags.length > 0) {
        seoData.hreflang_tags.forEach(tag => {
          if (tag.lang && tag.url) {
            const hreflangLink = document.createElement('link');
            hreflangLink.rel = 'alternate';
            hreflangLink.hreflang = tag.lang;
            hreflangLink.href = tag.url;
            document.head.appendChild(hreflangLink);
          }
        });
      }

      // Update Google Site Verification
      let googleVerification = document.querySelector('meta[name="google-site-verification"]');
      if (seoData.google_site_verification) {
        if (!googleVerification) {
          googleVerification = document.createElement('meta');
          googleVerification.name = 'google-site-verification';
          document.head.appendChild(googleVerification);
        }
        googleVerification.content = seoData.google_site_verification;
      } else if (googleVerification) {
        googleVerification.remove();
      }
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };

  const trackClick = async (brandId, clickType = "affiliate") => {
    try {
      await axios.post(`${API}/track-click`, { brand_id: brandId, click_type: clickType });
    } catch (e) {
      // Silent fail
    }
  };

  const handleCloseModal = () => {
    // Check if there's a next group to show
    const nextGroupIndex = currentGroupIndex + 1;
    
    if (nextGroupIndex < sequentialGroups.length) {
      // Show next group
      setCurrentGroupIndex(nextGroupIndex);
      setActiveModals(sequentialGroups[nextGroupIndex]);
      // Keep showModals true
    } else {
      // No more groups, close everything
      setShowModals(false);
      setActiveModals([]);
      setCurrentGroupIndex(0);
    }
  };

  const displayedBrands = expanded ? brands : brands.slice(0, 10);
  const hasMore = brands.length > 10;

  const faqItems = settings.faq_content ? (() => {
    try {
      return JSON.parse(settings.faq_content);
    } catch {
      return [];
    }
  })() : [];

  const ctaText = settings.cta_button_text || "BONUS AL";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d0d14] to-[#0a0a0f] flex flex-col">
      {/* Multiple Header Banners - Responsive */}
      {banners.length > 0 && (
        <div className="w-full py-1 sm:py-3 bg-gradient-to-r from-[#0f0f18] via-[#141420] to-[#0f0f18] border-b border-zinc-800/50" data-testid="header-banner-container">
          <div className="w-full sm:max-w-7xl sm:mx-auto sm:px-4">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-center gap-0.5 sm:gap-4">
              {banners.map((banner, idx) => (
                <a 
                  key={banner.id} 
                  href={banner.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="banner-item w-full sm:w-auto sm:flex-shrink-0 hover:scale-[1.02] sm:hover:scale-105 transition-transform duration-300"
                  onClick={() => trackClick(banner.id, "banner")}
                  data-testid={`banner-${idx}`}
                >
                  <img 
                    src={`${BACKEND_URL}${banner.image_url}`} 
                    alt={`Banner ${idx + 1}`} 
                    className="w-full sm:w-auto h-auto sm:h-[50px] md:h-[60px] sm:rounded-lg shadow-lg shadow-black/30 object-cover sm:object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="relative py-2 sm:py-6 md:py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-amber-500/5"></div>
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center mb-3 sm:mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 sm:px-4 sm:py-2 mb-2 sm:mb-4">
              <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
              <span className="text-emerald-400 text-xs sm:text-sm font-medium">{settings.hero_badge_text || "Güncel Casino İncelemeleri"}</span>
            </div>
            <h1 className="font-chivo font-black text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-white mb-2 sm:mb-3 md:mb-4 tracking-tight leading-tight" data-testid="site-title">
              {settings.hero_title || settings.site_name || "En İyi Casino Siteleri"}
            </h1>
            <SafeHtml
              className="text-zinc-400 text-sm sm:text-base md:text-lg max-w-2xl mx-auto hero-subtitle-html"
              data-testid="hero-subtitle"
              html={
                settings.hero_subtitle ||
                "Güvenilir ve lisanslı casino sitelerini keşfedin. En yüksek bonusları alın!"
              }
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 pb-6 sm:pb-8 md:pb-12 flex-1">
        {/* Casino List */}
        <div className="flex flex-col gap-3" data-testid="casino-list">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Yükleniyor...</div>
          ) : displayedBrands.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">Henüz casino eklenmemiş</div>
          ) : (
            displayedBrands.map((brand, index) => (
              brand.is_expired ? (
                <ExpiredSlot key={brand.id} order={index + 1} settings={settings} onContactClick={() => setShowContactModal(true)} />
              ) : (
                <CasinoRow key={brand.id} brand={brand} order={index + 1} onTrackClick={trackClick} ctaText={ctaText} />
              )
            ))
          )}
        </div>

        {/* Expand Button */}
        {hasMore && !expanded && (
          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => setExpanded(true)}
              className="border-zinc-700 text-zinc-400 hover:text-white hover:border-emerald-500 hover:bg-emerald-500/10"
              data-testid="expand-button"
            >
              Daha Fazla Göster ({brands.length - 10})
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Page Content */}
        {settings.page_content && (
          <div className="mt-16" data-testid="page-content">
            <SafeHtml 
              className="bg-zinc-900/50 rounded-2xl p-8 border border-zinc-800 content-html"
              html={settings.page_content}
            />
          </div>
        )}

        {/* FAQ Section */}
        {faqItems.length > 0 && (
          <div className="mt-16" data-testid="faq-section">
            <h2 className="font-chivo font-bold text-2xl text-white mb-6 text-center">Sıkça Sorulan Sorular</h2>
            <div className="space-y-3">
              {faqItems.map((faq, idx) => (
                <div 
                  key={idx} 
                  className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden"
                >
                  <button
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-zinc-800/50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  >
                    <span className="font-medium text-white">{faq.question}</span>
                    {openFaq === idx ? (
                      <ChevronUp className="h-5 w-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-zinc-400" />
                    )}
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-4 text-zinc-400">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-zinc-900/80 border-t border-zinc-800 mt-auto">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-zinc-400 text-sm text-center md:text-left">
              {settings.footer_text || `© ${new Date().getFullYear()} ${settings.site_name || "Casino Rehberi"}. Tüm hakları saklıdır.`}
            </div>
            <Button
              variant="outline"
              className="border-emerald-600 text-emerald-500 hover:bg-emerald-600 hover:text-white"
              onClick={() => setShowContactModal(true)}
              data-testid="footer-contact-btn"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              İletişime Geç
            </Button>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)} data-testid="contact-modal-overlay">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full mx-4 border border-zinc-700 animate-slideUp" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-chivo font-bold text-xl text-white">İletişim</h3>
              <button 
                onClick={() => setShowContactModal(false)}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              {settings.contact_email && (
                <a 
                  href={`mailto:${settings.contact_email}`}
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Mail className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">E-posta</div>
                    <div className="text-sm text-zinc-400">{settings.contact_email}</div>
                  </div>
                </a>
              )}
              {settings.whatsapp_link && (
                <a 
                  href={settings.whatsapp_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">WhatsApp</div>
                    <div className="text-sm text-zinc-400">Hızlı mesaj gönderin</div>
                  </div>
                </a>
              )}
              {settings.telegram_link && (
                <a 
                  href={settings.telegram_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-blue-400/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-blue-300" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Telegram</div>
                    <div className="text-sm text-zinc-400">Telegram'dan ulaşın</div>
                  </div>
                </a>
              )}
              {settings.teams_link && (
                <a 
                  href={settings.teams_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Microsoft Teams</div>
                    <div className="text-sm text-zinc-400">Teams üzerinden görüşün</div>
                  </div>
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Ads - Multiple modals stacked vertically */}
      {showModals && activeModals.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center pt-8 overflow-y-auto" onClick={handleCloseModal} data-testid="modal-ad-overlay">
          <div className="relative w-full max-w-2xl px-4 pb-8" onClick={e => e.stopPropagation()}>
            {/* Close button with group indicator */}
            <div className="fixed top-4 right-4 z-[60] flex items-center gap-3">
              {sequentialGroups.length > 1 && (
                <div className="bg-zinc-800/90 text-white px-3 py-2 rounded-full text-sm font-medium backdrop-blur-sm" data-testid="modal-group-indicator">
                  Grup {currentGroupIndex + 1} / {sequentialGroups.length}
                </div>
              )}
              <button 
                className="w-12 h-12 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg transition-colors"
                onClick={handleCloseModal} 
                data-testid="modal-close-btn"
              >
                {currentGroupIndex + 1 < sequentialGroups.length ? '→' : '✕'}
              </button>
            </div>
            
            {/* Stacked modals */}
            <div className="flex flex-col gap-6">
              {activeModals.map((modal, index) => (
                <div 
                  key={modal.id} 
                  className="bg-zinc-900 rounded-2xl overflow-hidden shadow-2xl border border-zinc-700 animate-slideUp"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <a 
                    href={modal.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackClick(modal.id, "modal")}
                    className="block"
                  >
                    <img 
                      src={`${BACKEND_URL}${modal.image_url}`} 
                      alt={`Reklam ${index + 1}`} 
                      className="w-full h-auto"
                    />
                  </a>
                </div>
              ))}
            </div>
            
            {/* Next group hint */}
            {currentGroupIndex + 1 < sequentialGroups.length && (
              <div className="text-center mt-4 text-zinc-400 text-sm" data-testid="modal-next-hint">
                Sonraki reklamları görmek için butona tıklayın
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CasinoRow = ({ brand, order, onTrackClick, ctaText }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const isTop3 = order <= 3;
  
  const handleCtaClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onTrackClick(brand.id, "affiliate");
    window.open(brand.affiliate_link, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <div 
      className={`casino-card group ${isTop3 ? 'featured' : ''}`}
      data-testid={`casino-row-${brand.id}`}
    >
      {/* Rank Badge */}
      <div className={`rank-number ${isTop3 ? 'top-rank' : ''}`}>
        {isTop3 && <Star className="h-3 w-3 absolute -top-1 -right-1 text-amber-400" />}
        {order}
      </div>
      
      {/* Logo - 100x40 - Affiliate link, yeni sekme */}
      <a
        href={brand.affiliate_link || `/casino/${brand.id}`}
        target={brand.affiliate_link ? "_blank" : "_self"}
        rel={brand.affiliate_link ? "noopener noreferrer sponsored" : ""}
        onClick={() => brand.affiliate_link && onTrackClick(brand.id, "affiliate")}
        className="logo-box hover:scale-105 transition-transform"
      >
        {brand.logo_url ? (
          <img src={`${BACKEND_URL}${brand.logo_url}`} alt={brand.name} />
        ) : (
          <span className="text-zinc-400 font-bold text-lg">{brand.name?.charAt(0)}</span>
        )}
      </a>

      {/* Info - Affiliate link, yeni sekme */}
      <a
        href={brand.affiliate_link || `/casino/${brand.id}`}
        target={brand.affiliate_link ? "_blank" : "_self"}
        rel={brand.affiliate_link ? "noopener noreferrer sponsored" : ""}
        onClick={() => brand.affiliate_link && onTrackClick(brand.id, "affiliate")}
        className="info-box hover:opacity-80 transition-opacity"
      >
        <h3 className="brand-name">{brand.name}</h3>
        <div className="bonus-badge">
          <Gift className="h-3.5 w-3.5" />
          <span>{brand.bonus}</span>
        </div>
      </a>

      {/* License */}
      <div className="license-box hidden md:flex">
        <Shield className="h-4 w-4 text-emerald-500" />
        <span>{brand.license}</span>
      </div>

      {/* CTA - Goes to affiliate link */}
      <button
        onClick={handleCtaClick}
        className="cta-btn"
        data-testid={`play-now-btn-${brand.id}`}
      >
        <span>{ctaText}</span>
        <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
      </button>
    </div>
  );
};

const ExpiredSlot = ({ order, settings, onContactClick }) => {
  return (
    <div className="casino-card expired" data-testid={`expired-slot-${order}`}>
      <div className="rank-number expired-rank">{order}</div>
      
      <div className="logo-box expired-logo">
        <span className="text-amber-500 text-2xl">?</span>
      </div>

      <div className="info-box">
        <h3 className="brand-name text-amber-500">Bu Alan Kiralanabilir</h3>
        <p className="text-zinc-500 text-sm">Markanızı burada öne çıkarın!</p>
      </div>

      <div className="contact-links">
        {settings.contact_email && (
          <a href={`mailto:${settings.contact_email}`} className="contact-link" onClick={e => e.stopPropagation()}>
            <Mail className="h-4 w-4" />
          </a>
        )}
        {settings.whatsapp_link && (
          <a href={settings.whatsapp_link} target="_blank" rel="noopener noreferrer" className="contact-link whatsapp" onClick={e => e.stopPropagation()}>
            <MessageCircle className="h-4 w-4" />
          </a>
        )}
      </div>

      <Button 
        variant="outline" 
        className="cta-btn-expired"
        onClick={(e) => {
          e.stopPropagation();
          if (onContactClick) {
            onContactClick();
          }
        }}
      >
        İletişime Geç
      </Button>
    </div>
  );
};

export default HomePage;